import { useParams } from "react-router-dom";
import useSWR, { mutate } from "swr";
import useSWRMutation from "swr/mutation";
import { BellIcon, CircleXIcon } from "lucide-react";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { putFetcher, useAuthStore, swrKey } from "@/lib/swr";
import PrdTable from "@/screens/Prds/prd-table";
import { tagDotColor } from "@/components/prds/tag-pill";
import { UserAvatar } from "@/components/user-chip";
import { AddButton } from "@/components/prds/add-button";
import type { PickerUser } from "@/components/user-selector";
import { UsersSelector } from "@/components/users-selector";
import { ListEmpty, ListLoading } from "@/components/list/status-text";
import type { TagItem, TagPerson } from "@/hooks/tags";

// Tag detail page: a heading for the label, then the PRDs carrying it.
// Members make the tag a lightweight project: tags you joined show up in the
// sidebar "Labels" group automatically (server-side membership, replacing the
// old local pin). 关注/取消关注 toggles yourself as a member. There is no owner
// — tags carry no permission or notification duty for one to hold.
// The PRD list reuses <PrdTable params={{ tagId }} />, so it inherits the existing
// visibility scoping (only PRDs under apps the user can see).
const TagDetail = () => {
  const { id } = useParams();
  const { data: tag, isLoading } = useSWR<TagItem>(id ? [`/tags/${id}`] : null);
  const me = useAuthStore((s) => s.auth?.user);
  const { trigger: save, isMutating } = useSWRMutation(`/tags/${id}`, putFetcher);

  if (isLoading) return <ListLoading>Loading label…</ListLoading>;
  if (!tag)
    return <ListEmpty>Label not found.</ListEmpty>;

  const members: TagPerson[] = tag.members ?? [];
  const joined = !!me && members.some((m) => m.id === me.id);

  const refresh = () => {
    mutate(swrKey(`/tags/${id}`));
    mutate(swrKey("/tags", { mine: 1 }));
  };

  const saveMembers = async (users: PickerUser[]) => {
    await save({ memberIds: users.map((u) => String(u.id)) });
    refresh();
  };

  const toggleJoin = async () => {
    if (!me) return;
    const ids = members.map((m) => m.id);
    await save({
      memberIds: joined ? ids.filter((x) => x !== me.id) : [...ids, me.id],
    });
    refresh();
  };

  return (
    <div>
      <PageBreadcrumb parent={{ label: "Labels" }} current={tag.name} />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            "h-3 w-3 rounded-full",
            tag.color ?? tagDotColor(tag.name)
          )}
        />
        <h1 className="text-lg font-semibold text-foreground">{tag.name}</h1>

        <div className="ml-auto flex items-center gap-2">
          {/* Members read as a plain avatar stack (who's watching this label),
              not a button; adding sits behind a separate "+" that stays out of
              the way until you hover the stack. */}
          <div className="group/members flex items-center gap-1.5">
            {members.length > 0 ? (
              <span className="flex -space-x-1.5">
                {members.map((m) => (
                  <span key={m.id} title={m.name}>
                    <UserAvatar
                      user={m}
                      className="h-5 w-5 ring-1 ring-background"
                    />
                  </span>
                ))}
              </span>
            ) : (
              <span className="text-sm text-foreground/70">Members</span>
            )}
            <UsersSelector
              selectedUsers={members}
              onContinue={saveMembers}
              trigger={
                <AddButton
                  title="Add member"
                  className={cn(
                    members.length > 0 &&
                      "opacity-0 transition-opacity focus-visible:opacity-100 group-hover/members:opacity-100"
                  )}
                />
              }
            />
          </div>
          <Button
            variant={joined ? "destructive" : "default"}
            size="sm"
            className="gap-1.5"
            disabled={isMutating || !me}
            onClick={toggleJoin}
          >
            {joined ? (
              <CircleXIcon className="h-3.5 w-3.5" />
            ) : (
              <BellIcon className="h-3.5 w-3.5" />
            )}
            {joined ? "取消关注" : "关注"}
          </Button>
        </div>
      </div>

      {/* Filter by id, not name — names are editable, ids are stable. */}
      <PrdTable params={{ tagId: tag.id }} hideCreate />
    </div>
  );
};

export default TagDetail;
